import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Eye, EyeOff, ArrowLeft, Loader2, Shield } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const PENDING_IMPORT_KEY = 'ff_pending_import';

// 24h TTL — anything older is discarded so a stale import doesn't surprise the user.
const PENDING_IMPORT_TTL_MS = 24 * 60 * 60 * 1000;

const claimPendingImport = async (token) => {
  let raw;
  try { raw = localStorage.getItem(PENDING_IMPORT_KEY); } catch { return null; }
  if (!raw) return null;
  let parsed;
  try { parsed = JSON.parse(raw); } catch {
    try { localStorage.removeItem(PENDING_IMPORT_KEY); } catch { /* ignore */ }
    return null;
  }
  if (!parsed || !Array.isArray(parsed.fields) || parsed.fields.length === 0) {
    try { localStorage.removeItem(PENDING_IMPORT_KEY); } catch { /* ignore */ }
    return null;
  }
  if (parsed.stashed_at && Date.now() - parsed.stashed_at > PENDING_IMPORT_TTL_MS) {
    try { localStorage.removeItem(PENDING_IMPORT_KEY); } catch { /* ignore */ }
    return null;
  }
  try {
    const { data } = await axios.post(
      `${API}/api/tools/import-form-save`,
      {
        platform: parsed.platform,
        source_url: parsed.source_url,
        title: parsed.title,
        fields: parsed.fields,
      },
      { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 }
    );
    try { localStorage.removeItem(PENDING_IMPORT_KEY); } catch { /* ignore */ }
    return data?.form_id || null;
  } catch {
    // Leave the pending import in place so the user can try again from the dashboard.
    return null;
  }
};

const Login = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    if (mfaRequired && (!mfaCode || mfaCode.length !== 6)) {
      toast.error('Please enter a valid 6-digit authenticator code');
      return;
    }
    
    setLoading(true);
    try {
      const result = await login(email, password, mfaRequired ? mfaCode : null);
      
      if (result.mfa_required) {
        setMfaRequired(true);
        setLoading(false);
        return;
      }
      
      toast.success(`Welcome back, ${result.name}!`);

      // If the visitor came from a `/tools/import-*` page, finalize the form
      // creation now that they're authenticated, and route to the builder.
      const newToken = (() => { try { return localStorage.getItem('ff_token'); } catch { return null; } })();
      if (newToken) {
        const importedFormId = await claimPendingImport(newToken);
        if (importedFormId) {
          toast.success('Your imported form is ready');
          navigate(`/builder/${importedFormId}`);
          return;
        }
      }

      const redirectTo = searchParams.get('redirect');
      const action = searchParams.get('action');
      if (redirectTo) {
        // Preserve action parameter when redirecting (e.g., for template customize)
        const redirectUrl = action ? `${redirectTo}?action=${action}` : redirectTo;
        navigate(redirectUrl);
      } else if (result.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Invalid email or password';
      toast.error(detail);
      // If MFA code was wrong, keep the MFA view but clear the code
      if (mfaRequired) {
        setMfaCode('');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900 mb-8 transition-colors"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>
        
        <Card className="border-zinc-200 shadow-lg">
          <CardHeader className="text-center pb-2">
            <img src="/logo.png" alt="Fantastic Forms" className="mx-auto w-12 h-12 mb-4" />
            {mfaRequired ? (
              <>
                <div className="mx-auto w-12 h-12 bg-zinc-100 rounded-full flex items-center justify-center mb-2">
                  <Shield className="w-6 h-6 text-zinc-700" />
                </div>
                <CardTitle className="text-2xl font-bold">Two-factor authentication</CardTitle>
                <CardDescription>Enter the 6-digit code from your authenticator app</CardDescription>
              </>
            ) : (
              <>
                <CardTitle className="text-2xl font-bold">Welcome back</CardTitle>
                <CardDescription>Sign in to your Fantastic Forms account</CardDescription>
              </>
            )}
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!mfaRequired ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={loading}
                      data-testid="login-email-input"
                      className="h-11"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password">Password</Label>
                      <Link 
                        to="/forgot-password" 
                        className="text-xs text-zinc-500 hover:text-zinc-900"
                        data-testid="forgot-password-link"
                      >
                        Forgot password?
                      </Link>
                    </div>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={loading}
                        data-testid="login-password-input"
                        className="h-11 pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                        data-testid="toggle-password-visibility"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="mfa-code">Authenticator Code</Label>
                  <Input
                    id="mfa-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="000000"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    disabled={loading}
                    data-testid="mfa-code-input"
                    className="h-11 text-center text-2xl tracking-widest"
                    maxLength={6}
                    autoFocus
                  />
                  <p className="text-xs text-zinc-500 text-center mt-2">
                    Open your authenticator app and enter the code shown for Fantastic Forms.
                  </p>
                </div>
              )}
              
              <Button
                type="submit"
                className="w-full h-11 bg-zinc-900 hover:bg-zinc-800"
                disabled={loading || (mfaRequired && mfaCode.length !== 6)}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {mfaRequired ? 'Verifying...' : 'Signing in...'}
                  </>
                ) : (
                  mfaRequired ? 'Verify & Sign in' : 'Sign in'
                )}
              </Button>

              {mfaRequired && (
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => {
                    setMfaRequired(false);
                    setMfaCode('');
                  }}
                  data-testid="mfa-back-btn"
                >
                  Back to login
                </Button>
              )}
            </form>

            {/* Continue with SSO — kicks off /api/sso/authorize which
                looks up the workspace by the email's domain and
                redirects to the IdP. Hidden while MFA is pending. */}
            {!mfaRequired && (
              <>
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-zinc-200" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase tracking-wide">
                    <span className="bg-white px-2 text-zinc-400">or</span>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  disabled={!email}
                  onClick={() => {
                    const API_BASE = process.env.REACT_APP_BACKEND_URL;
                    const q = new URLSearchParams({ email }).toString();
                    window.location.href = `${API_BASE}/api/sso/authorize?${q}`;
                  }}
                  data-testid="login-sso-btn"
                >
                  Continue with SSO
                </Button>
                <p className="mt-2 text-center text-[11px] text-zinc-400">
                  Enter your work email above, then click &ldquo;Continue with SSO&rdquo;.
                </p>
              </>
            )}
            
            {!mfaRequired && (
              <div className="mt-6 text-center text-sm text-zinc-500">
                Don't have an account?{' '}
                <Link 
                  to="/signup" 
                  className="text-zinc-900 font-medium hover:underline"
                  data-testid="signup-link"
                >
                  Sign up
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
